import {
  Box,
  Stack,
  Table,
  TableBody,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ControlViolations, BackupStatus } from '@repo/api-gw-sdk';
import type React from 'react';

import { BorderlessTableCell } from '@/components/table/components';
import { BackupStatuses } from '@/data/inventory/backupStatuses';
import { ControlsSeverity } from '@/data/inventory/controlSeverities';

import { BackupStatusIcon } from '../backupStatus/backupStatusIcon';

export const ControlIndicator = ({
  backgroundColor,
}: {
  backgroundColor: string;
}) => {
  return (
    <Box
      component={'span'}
      className='rounded-full w-[8px] h-[8px] m-[8px] inline-block'
      sx={{
        backgroundColor,
        // The dot sits in a flex row beside a label long enough to wrap, which
        // would otherwise squeeze it out of round.
        flexShrink: 0,
        minWidth: '8px',
        minHeight: '8px',
      }}
    />
  );
};

export const controlKeysPriorities: (keyof ControlViolations)[] = [
  'high',
  'medium',
  'low',
  'muted',
];

// The severity breakdown any axis that reports on violations can hang off its icon: the status
// name, then one row per non-zero severity. Rendered as the Tooltip's title rather than around it,
// so a resource with no violations still gets the plain name instead of losing the tooltip.
const ControlViolationsBreakdown = ({
  title,
  controlViolations,
}: {
  title: string;
  controlViolations: ControlViolations;
}) => (
  <Stack>
    <Typography
      className='mb-[8px]'
      sx={{
        color: 'var(--mui-palette-Tooltip-text)',
      }}
    >
      {title}
    </Typography>
    <Table>
      <TableBody>
        {controlKeysPriorities.map((priority) => {
          const amount = controlViolations[priority];
          return (
            !!amount && (
              <TableRow key={priority} className='mt-[4px] py-[2px]'>
                <BorderlessTableCell>
                  <ControlIndicator
                    backgroundColor={ControlsSeverity[priority].color}
                  />
                </BorderlessTableCell>
                <BorderlessTableCell>
                  <Typography
                    className='ml-[8px]'
                    sx={{
                      color: 'var(--mui-palette-Tooltip-text)',
                    }}
                  >
                    {amount}
                  </Typography>
                </BorderlessTableCell>
                <BorderlessTableCell>
                  <Typography
                    className='ml-[8px]'
                    sx={{
                      color: 'var(--mui-palette-Tooltip-text)',
                    }}
                  >
                    {ControlsSeverity[priority].title}
                  </Typography>
                </BorderlessTableCell>
              </TableRow>
            )
          );
        })}
      </TableBody>
    </Table>
  </Stack>
);

export const ControlViolationsTooltip = ({
  title,
  controlViolations,
  children,
}: {
  title: string;
  controlViolations: ControlViolations | null | undefined;
  children: React.ReactElement;
}) => (
  <Tooltip
    disableInteractive={!controlViolations}
    title={
      controlViolations ? (
        <ControlViolationsBreakdown
          title={title}
          controlViolations={controlViolations}
        />
      ) : (
        title
      )
    }
    className='ml-[8px]'
    slotProps={{
      tooltip: {
        sx: {
          width: controlViolations ? '170px' : undefined,
          padding: controlViolations ? '16px' : undefined,
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

export const ControlsColumnIndicators = ({
  controlViolations,
  backupStatus,
}: {
  controlViolations: ControlViolations | null | undefined;
  backupStatus: BackupStatus;
}) => {
  const data = BackupStatuses[backupStatus];
  if (!data) {
    return null;
  }

  return (
    <ControlViolationsTooltip
      title={data.title}
      controlViolations={controlViolations}
    >
      <BackupStatusIcon backupStatus={backupStatus} />
    </ControlViolationsTooltip>
  );
};
